import { useQuery, useQueries } from '@tanstack/react-query';
import { UserKeys } from '../../../api/users-api';
import {
  VaultKeys,
  getAllVaultMatters,
  getAllVaultExports,
  VaultMatterModel,
  VaultExportModel,
  VaultExportFileModel,
  checkVaultAccess,
} from '../../../api/vault-api';
import VaultExportGrid, { ExtendedVaultExportModel } from './vault-export-grid';
import CreateVaultMatterDialog from './create-vault-matter-dialog';
import EditVaultMatterDialog from './edit-vault-matter-dialog';
import DeleteVaultMatterDialog from './delete-vault-matter-dialog';
import CreateVaultExportDialog from './create-vault-export-dialog';
import MatterDetails from './matter-details';
import VaultMattersTileView from './vault-matters-tile-view';
import VaultMattersTableView from './vault-matters-table-view';
import VaultLicenseError from '../../automation/decommissioning/decom-policy-form/form-steps/shared/vault-license-error';
import { pagesConfig } from '../../../shared/pages-config';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import { getDeleteMatterPermissionState } from '../../../utils/vault-utils';
import { DisabledTooltipMenuItem } from '../../../shared/datagrids/tooltip-grid-actions-cell-item';
import SimpleBreadcrumbsPage from '../../../shared/simple-breadcrumbs-page';
import { ModuleList } from '../../../shared/analytics/module-list';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import {
  isAbleToSeeUIElement,
  VaultRoles,
} from '../../../utils/roles/client-roles';
import { StatCard } from '../../../shared/stat-card';
import { useState, MouseEvent, ChangeEvent, useMemo, useEffect } from 'react';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import TablePagination from '@mui/material/TablePagination';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import GetAppOutlined from '@mui/icons-material/GetAppOutlined';
import SharedTabCard from '../../../shared/shared-tab-card';
import { useTheme } from '@mui/material/styles';

export interface ExtendedVaultMatterModel extends VaultMatterModel {
  exports?: VaultExportModel[];
  exportCount: number;
  dataSizeBytes: number;
  exportsRunning: number;
  exportsFailed: number;
}

export default function VaultExportPage() {
  const theme = useTheme();
  const { toolsRoles, features, viewType } = useUserSettings();

  const canViewMatters = useMemo(() => {
    return (
      toolsRoles.includes('viewVaultMatters') ||
      toolsRoles.includes('viewAllVault') ||
      toolsRoles.includes('*')
    );
  }, [toolsRoles]);

  const canViewExports = useMemo(() => {
    const granularVaultViewRoles = [
      'viewGmailVaultExport',
      'viewChatVaultExport',
      'viewGeminiVaultExport',
      'viewCalendarVaultExport',
      'viewDriveVaultExport',
      'viewGroupsVaultExport',
      'viewAllVault',
      'viewVaultExports',
    ];
    return (
      granularVaultViewRoles.some((role) => toolsRoles.includes(role)) ||
      toolsRoles.includes('*')
    );
  }, [toolsRoles]);

  const [selectedMatters, setSelectedMatters] = useState<string[]>([]);
  const [selectedMatterId, setSelectedMatterId] = useState<string>('');
  const [openAddMatterDialog, setOpenAddMatterDialog] = useState(false);
  const [openEditMatterDialog, setOpenEditMatterDialog] = useState(false);
  const [openDeleteMatterDialog, setOpenDeleteMatterDialog] = useState(false);
  const [openAddVaultExport, setOpenAddVaultExport] = useState(false);
  const [activeTab, setActiveTab] = useState<'matters' | 'allExports'>(
    'matters'
  );

  useEffect(() => {
    if (!canViewMatters && activeTab === 'matters' && canViewExports) {
      setActiveTab('allExports');
    }
  }, [canViewMatters, activeTab, canViewExports]);

  const [exportFilter, setExportFilter] = useState<'all' | 'running'>('all');
  const [viewMode, setViewMode] = useState<'list' | 'details'>('list');
  const [matterView, setMatterView] = useState<'tile' | 'table'>('tile');
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [actionMenuMatterId, setActionMenuMatterId] = useState<string>('');
  const [tilePage, setTilePage] = useState(0);
  const [tileRowsPerPage, setTileRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

  const isExportPermitted = useMemo(() => {
    return (exp: VaultExportModel) => {
      const hasViewAll =
        toolsRoles.includes('viewVaultExports') ||
        toolsRoles.includes('viewAllVault') ||
        toolsRoles.includes('*');
      if (hasViewAll) return true;

      const corpus = exp?.corpus;
      if (!corpus) return false;
      switch (corpus) {
        case 'MAIL':
          return isAbleToSeeUIElement(
            toolsRoles,
            VaultRoles.ViewGmailVaultExport.value
          );
        case 'HANGOUTS_CHAT':
          return isAbleToSeeUIElement(
            toolsRoles,
            VaultRoles.ViewChatVaultExport.value
          );
        case 'GEMINI':
          return isAbleToSeeUIElement(
            toolsRoles,
            VaultRoles.ViewGeminiVaultExport.value
          );
        case 'CALENDAR':
          return isAbleToSeeUIElement(
            toolsRoles,
            VaultRoles.ViewCalendarVaultExport.value
          );
        case 'DRIVE':
          return isAbleToSeeUIElement(
            toolsRoles,
            VaultRoles.ViewDriveVaultExport.value
          );
        case 'GROUPS':
          return isAbleToSeeUIElement(
            toolsRoles,
            VaultRoles.ViewGroupsVaultExport.value
          );
        default:
          return false;
      }
    };
  }, [toolsRoles]);

  const canCreateMatter = useMemo(() => {
    return isAbleToSeeUIElement(
      toolsRoles,
      VaultRoles.CreateVaultMatters.value
    );
  }, [toolsRoles]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const {
    data: hasVaultAccess,
    isLoading: isLoadingVaultAccess,
    isError: isAccessError,
  } = useQuery({
    queryKey: [UserKeys.AdminApiSettings, 'vault'],
    queryFn: () => checkVaultAccess(),
    staleTime: 1000 * 60 * 60, // 1 hour
    enabled: isFeatureEnabled('vaultExportReact', features),
  });

  const { data: rawVaultMatters = [], isLoading: vaultMattersLoading } =
    useQuery({
      queryKey: [VaultKeys.VaultMatters],
      queryFn: getAllVaultMatters,
      meta: {
        errorMessage: Messages.Errors.Network.Loading('vault matters'),
      },
      enabled:
        isFeatureEnabled('vaultExportReact', features) && !!hasVaultAccess,
      staleTime: 1000 * 60 * 5,
    });

  const isLoading = isLoadingVaultAccess || vaultMattersLoading;

  const exportsQueries = useQueries({
    queries: rawVaultMatters.map((matter) => ({
      queryKey: [VaultKeys.VaultExports, matter.matterId],
      queryFn: () => getAllVaultExports(matter.matterId),
      enabled: canViewExports && !!matter.matterId && matter.state === 'OPEN',
    })),
  });

  // useQueries returns a new array reference on each render; extract
  // the stable pieces we care about to avoid unstable deps in useMemo
  const exportsQueriesMeta = exportsQueries.map((q) => ({
    data: q?.data,
    status: q?.status,
    isFetching: q?.isFetching,
  }));

  const vaultMatters = useMemo(() => {
    return rawVaultMatters.map((matter, index) => {
      const query = exportsQueriesMeta[index];
      const rawExports = query?.data?.results || [];
      const exports = rawExports.filter(isExportPermitted);
      const exportCount = exports.length;
      const dataSizeBytes = exports.reduce((sum, exp) => {
        const files = exp.cloudStorageSink?.files || [];
        const computedSize = files.reduce(
          (s: number, f: VaultExportFileModel) => s + (f?.size ?? 0),
          0
        );
        return sum + computedSize;
      }, 0);
      const exportsRunning = exports.filter(
        (exp) => exp.status === 'IN_PROGRESS'
      ).length;
      const exportsFailed = exports.filter(
        (exp) => exp.status === 'FAILED'
      ).length;

      return {
        ...matter,
        exports: rawExports,
        exportCount,
        dataSizeBytes,
        exportsRunning,
        exportsFailed,
      };
    });
  }, [rawVaultMatters, exportsQueriesMeta, isExportPermitted]);

  const isPageLoading =
    isLoading || (canViewExports && exportsQueries.some((q) => q.isLoading));

  const allExports = useMemo(() => {
    if (!canViewExports) return [];
    return rawVaultMatters.flatMap((matter, index) => {
      const query = exportsQueriesMeta[index];
      const rawExports = query?.data?.results || [];
      const exports = rawExports.filter(isExportPermitted);
      return exports.map((exp) => {
        const files = exp.cloudStorageSink?.files || [];
        const computedSize = files.reduce(
          (s: number, f: VaultExportFileModel) => s + (f?.size ?? 0),
          0
        );
        return {
          ...exp,
          matterName: matter?.name || '—',
          totalArtifactCount: exp.exportStats?.totalArtifactCount,
          dataSizeBytes: computedSize,
        } as ExtendedVaultExportModel;
      });
    });
  }, [rawVaultMatters, exportsQueriesMeta, isExportPermitted, canViewExports]);

  const filteredExports = useMemo(() => {
    if (exportFilter === 'running') {
      return allExports.filter((exp) => exp.status === 'IN_PROGRESS');
    }
    return allExports;
  }, [allExports, exportFilter]);

  const paginatedExports = useMemo(() => {
    return filteredExports.slice(
      tilePage * tileRowsPerPage,
      (tilePage + 1) * tileRowsPerPage
    );
  }, [filteredExports, tilePage, tileRowsPerPage]);

  const filteredMatters = useMemo(() => {
    const baseMatters = vaultMatters;

    if (!debouncedSearchTerm) {
      return baseMatters;
    }
    const term = debouncedSearchTerm.toLowerCase();
    return baseMatters.filter(
      (matter) =>
        matter.name?.toLowerCase().includes(term) ||
        matter.description?.toLowerCase().includes(term) ||
        matter.state?.toLowerCase().includes(term)
    );
  }, [vaultMatters, debouncedSearchTerm]);

  const handleChangePage = (
    _event: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setTilePage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setTileRowsPerPage(newRowsPerPage);
    setTilePage(0);
  };

  const paginatedMatters = filteredMatters.slice(
    tilePage * tileRowsPerPage,
    (tilePage + 1) * tileRowsPerPage
  );

  const openMattersCount = vaultMatters.filter(
    (matter) => matter.state === 'OPEN'
  ).length;
  const totalExportCount = vaultMatters.reduce(
    (sum, matter) => sum + (matter.exportCount ?? 0),
    0
  );
  const exportsRunningCount = vaultMatters.reduce(
    (sum, matter) => sum + (matter.exportsRunning ?? 0),
    0
  );

  const handleOpenActionMenu = (
    event: MouseEvent<HTMLElement>,
    matterId: string
  ) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuMatterId(matterId);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuMatterId('');
  };

  const handleSelectMatter = (matterId: string) => {
    setSelectedMatterId(matterId);
    setViewMode('details');
  };

  const handleEditMatter = (matterId: string) => {
    setSelectedMatterId(matterId);
    setOpenEditMatterDialog(true);
  };

  const selectedMatter = vaultMatters.find(
    (matter) => matter.matterId === selectedMatterId
  );

  if (hasVaultAccess === false || isAccessError) {
    return (
      <SimpleBreadcrumbsPage
        breadcrumbs={[
          { label: ModuleList.modules.importExport.name },
          { label: pagesConfig.vaultExport.name },
        ]}
        bookmarkKey={pagesConfig.vaultExport.value}
      >
        <VaultLicenseError />
      </SimpleBreadcrumbsPage>
    );
  }

  if (viewMode === 'details' && selectedMatter) {
    return (
      <>
        <SimpleBreadcrumbsPage
          breadcrumbs={[
            { label: ModuleList.modules.importExport.name },
            {
              label: pagesConfig.vaultExport.name,
              path: pagesConfig.vaultExport.link,
              onClick: () => {
                setViewMode('list');
                setSelectedMatterId('');
              },
            },
            { label: 'Matter Details' },
          ]}
          bookmarkKey={pagesConfig.vaultExport.value}
        >
          <MatterDetails
            matter={selectedMatter}
            onBack={() => {
              setViewMode('list');
              setSelectedMatterId('');
            }}
            onDelete={() => setOpenDeleteMatterDialog(true)}
            onEdit={() => setOpenEditMatterDialog(true)}
            toolsRoles={toolsRoles}
            openAddVaultExport={openAddVaultExport}
            setOpenAddVaultExport={setOpenAddVaultExport}
          />
        </SimpleBreadcrumbsPage>

        {openEditMatterDialog && selectedMatter && (
          <EditVaultMatterDialog
            open={openEditMatterDialog}
            handleClose={() => setOpenEditMatterDialog(false)}
            setSelectedMatterId={setSelectedMatterId}
            matter={selectedMatter}
          />
        )}

        {openDeleteMatterDialog && selectedMatterId && (
          <DeleteVaultMatterDialog
            open={openDeleteMatterDialog}
            handleClose={() => setOpenDeleteMatterDialog(false)}
            selectedMatterIds={[selectedMatterId]}
            vaultMatters={vaultMatters}
            setSelectedMatters={() => {
              setSelectedMatterId('');
              setSelectedMatters([]);
              setViewMode('list');
            }}
          />
        )}
      </>
    );
  }

  return (
    <SimpleBreadcrumbsPage
      breadcrumbs={[
        { label: ModuleList.modules.importExport.name },
        { label: pagesConfig.vaultExport.name },
      ]}
      bookmarkKey={pagesConfig.vaultExport.value}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2, width: '100%' }}
          >
            <StatCard
              color="teal"
              label="Open Matters"
              value={canViewMatters ? openMattersCount : 0}
              icon={<FolderOutlinedIcon />}
              active={canViewMatters && activeTab === 'matters'}
              disabled={!canViewMatters || activeTab === 'matters'}
              onClick={() => {
                if (!canViewMatters) return;
                setActiveTab('matters');
                setTilePage(0);
              }}
              sx={{
                flex: 1,
                minWidth: 240,
                ...(!canViewMatters && { opacity: 0.5, cursor: 'not-allowed' }),
              }}
            />
            <StatCard
              color="cyan"
              label="Total Exports"
              value={canViewExports ? totalExportCount : 0}
              icon={<GetAppOutlined />}
              active={
                canViewExports &&
                activeTab === 'allExports' &&
                exportFilter === 'all'
              }
              disabled={
                !canViewExports ||
                (activeTab === 'allExports' && exportFilter === 'all')
              }
              onClick={() => {
                if (!canViewExports) return;
                setActiveTab('allExports');
                setExportFilter('all');
                setTilePage(0);
              }}
              sx={{
                flex: 1,
                minWidth: 240,
                ...(!canViewExports && { opacity: 0.5, cursor: 'not-allowed' }),
              }}
            />
            <StatCard
              color="orange"
              label="Exports Running"
              value={canViewExports ? exportsRunningCount : 0}
              icon={<TrendingUpIcon />}
              active={
                canViewExports &&
                activeTab === 'allExports' &&
                exportFilter === 'running'
              }
              disabled={
                !canViewExports ||
                (activeTab === 'allExports' && exportFilter === 'running')
              }
              onClick={() => {
                if (!canViewExports) return;
                setActiveTab('allExports');
                setExportFilter('running');
                setTilePage(0);
              }}
              sx={{
                flex: 1,
                minWidth: 240,
                ...(!canViewExports && { opacity: 0.5, cursor: 'not-allowed' }),
              }}
            />
          </Stack>
          <SharedTabCard>
            <Tabs
              value={activeTab}
              onChange={(_, value) => {
                if (value === 'allExports' && !canViewExports) return;
                if (value === 'matters' && !canViewMatters) return;
                setActiveTab(value);
                setTilePage(0);
                if (value === 'allExports') {
                  setExportFilter('all');
                }
              }}
              textColor="primary"
              indicatorColor="primary"
              sx={{
                mx: 1,
                '& .MuiTabs-flexContainer': {
                  marginTop: '6px',
                },
                '.MuiTabs-scrollButtons.Mui-disabled': {
                  opacity: 0.3,
                },
              }}
            >
              {canViewMatters && (
                <Tab
                  icon={<FolderOutlinedIcon />}
                  iconPosition="start"
                  value="matters"
                  label={`Matters (${vaultMatters.length})`}
                  sx={{ minHeight: '40px', height: '40px' }}
                />
              )}
              {canViewExports && (
                <Tab
                  icon={<GetAppOutlined />}
                  iconPosition="start"
                  value="allExports"
                  label={`All Exports (${allExports.length})`}
                  sx={{ minHeight: '40px', height: '40px' }}
                />
              )}
            </Tabs>
          </SharedTabCard>
          {activeTab === 'matters' ? (
            <Stack flexDirection="column" sx={{ flex: 1, minHeight: 0 }}>
              {matterView === 'tile' ? (
                <VaultMattersTileView
                  isPageLoading={isPageLoading}
                  paginatedMatters={paginatedMatters}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  setTilePage={setTilePage}
                  setSelectedMatterId={setSelectedMatterId}
                  setOpenAddMatterDialog={setOpenAddMatterDialog}
                  setOpenAddVaultExport={setOpenAddVaultExport}
                  matterView={matterView}
                  setMatterView={setMatterView}
                  handleOpenActionMenu={handleOpenActionMenu}
                  handleSelectMatter={handleSelectMatter}
                  canCreateMatter={canCreateMatter}
                />
              ) : (
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <VaultMattersTableView
                    toolsRoles={toolsRoles}
                    isPageLoading={isPageLoading}
                    paginatedMatters={paginatedMatters}
                    selectedMatters={selectedMatters}
                    setSelectedMatters={setSelectedMatters}
                    setOpenDeleteMatterDialog={setOpenDeleteMatterDialog}
                    setOpenAddMatterDialog={setOpenAddMatterDialog}
                    setSelectedMatterId={setSelectedMatterId}
                    handleSelectMatter={handleSelectMatter}
                    handleEditMatter={handleEditMatter}
                    matterView={matterView}
                    setMatterView={setMatterView}
                    viewType={viewType}
                    canCreateMatter={canCreateMatter}
                  />
                </Box>
              )}

              {(() => {
                const actionMenuMatter = vaultMatters.find(
                  (m) => m.matterId === actionMenuMatterId
                );
                const deleteStateForActionMenuMatter = actionMenuMatter
                  ? getDeleteMatterPermissionState(toolsRoles)
                  : { canDelete: true, tooltip: '' };

                return (
                  <Menu
                    anchorEl={actionMenuAnchor}
                    open={Boolean(actionMenuAnchor)}
                    onClose={handleCloseActionMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <DisabledTooltipMenuItem
                      disabled={
                        !isAbleToSeeUIElement(
                          toolsRoles,
                          VaultRoles.EditVaultMatters.value
                        )
                      }
                      data-disabled-reason={Messages.Errors.Permission.Acting(
                        VaultRoles.EditVaultMatters.name
                      )}
                      onClick={() => {
                        if (actionMenuMatterId) {
                          handleEditMatter(actionMenuMatterId);
                        }
                        handleCloseActionMenu();
                      }}
                      iconStart={<Edit fontSize="small" />}
                    >
                      Edit
                    </DisabledTooltipMenuItem>
                    <DisabledTooltipMenuItem
                      disabled={!deleteStateForActionMenuMatter.canDelete}
                      data-disabled-reason={
                        deleteStateForActionMenuMatter.tooltip
                      }
                      onClick={() => {
                        if (actionMenuMatterId) {
                          setSelectedMatterId(actionMenuMatterId);
                          setOpenDeleteMatterDialog(true);
                        }
                        handleCloseActionMenu();
                      }}
                      iconStart={<Delete fontSize="small" />}
                    >
                      Delete
                    </DisabledTooltipMenuItem>
                  </Menu>
                );
              })()}
            </Stack>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <VaultExportGrid
                toolsRoles={toolsRoles}
                matterId={selectedMatterId}
                allExports={paginatedExports}
                loading={isPageLoading}
              />
            </Box>
          )}
        </Box>

        {(activeTab === 'matters' || activeTab === 'allExports') && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: 'background.paper',
              border: theme.palette.mode === 'dark' ? '0.5px solid' : 0,
              borderTop: 0.5,
              borderColor: theme.palette.semantic.outline.light,
              position:
                activeTab === 'matters' && matterView === 'table'
                  ? 'sticky'
                  : 'relative',
              bottom: 0,
              zIndex: 1,
              px: 2,
              borderBottomLeftRadius: '8px',
              WebkitBorderBottomRightRadius: '8px',
            }}
          >
            {activeTab === 'matters' && matterView === 'table' ? (
              <Typography variant="body2" color="text.secondary">
                {selectedMatters.length > 0
                  ? `${selectedMatters.length} row${
                      selectedMatters.length > 1 ? 's' : ''
                    } selected`
                  : ''}
              </Typography>
            ) : (
              <Box />
            )}
            <TablePagination
              component="div"
              count={
                activeTab === 'matters'
                  ? filteredMatters.length
                  : filteredExports.length
              }
              page={tilePage}
              onPageChange={handleChangePage}
              rowsPerPage={tileRowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </Box>
        )}
      </Box>

      {openAddMatterDialog && (
        <CreateVaultMatterDialog
          open={openAddMatterDialog}
          handleClose={() => {
            setOpenAddMatterDialog(false);
            // If a matter was selected for creation, but cancelled, clear it
            setSelectedMatterId('');
          }}
          setSelectedMatterId={setSelectedMatterId}
        />
      )}

      {openEditMatterDialog && selectedMatter && (
        <EditVaultMatterDialog
          open={openEditMatterDialog}
          handleClose={() => {
            setOpenEditMatterDialog(false);
            setSelectedMatterId('');
          }}
          setSelectedMatterId={setSelectedMatterId}
          matter={selectedMatter}
        />
      )}

      {openDeleteMatterDialog &&
        (selectedMatterId || selectedMatters.length > 0) && (
          <DeleteVaultMatterDialog
            open={openDeleteMatterDialog}
            handleClose={() => {
              setOpenDeleteMatterDialog(false);
              if (selectedMatterId) {
                setSelectedMatterId('');
              }
            }}
            selectedMatterIds={
              selectedMatterId ? [selectedMatterId] : selectedMatters
            }
            vaultMatters={vaultMatters}
            setSelectedMatters={(ids) => {
              if (selectedMatterId) {
                setSelectedMatterId('');
              }
              setSelectedMatters(ids);
            }}
          />
        )}

      {openAddVaultExport && selectedMatterId && (
        <CreateVaultExportDialog
          open={openAddVaultExport}
          handleClose={() => setOpenAddVaultExport(false)}
          matterId={selectedMatterId}
          matterName={selectedMatter?.name}
        />
      )}
    </SimpleBreadcrumbsPage>
  );
}
